import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl, IntlShape } from 'react-intl';
import FileSaver from 'file-saver';
import { write as XLSXWrite, utils as XLSXUtils } from 'xlsx';
import { ContextValues } from "@42maru/webchat";
import {
    DictType, LabelKeyValueItem,
    MessageChannel, MessageFeedback, MessageHistoryCustom, MessageHistorySearchType, SearchedMessage
} from '@/model';
import { UTCToLocalString } from '@/util/dateUtil';
import { getTextAndLabelFromResponse, getValueFromDotJoinKey, getValueFromGlobalContext } from '@/util/messageUtil';
import { getLabelKeyFromValue } from '@/util/typeUtil';
import { useScenario } from '@/query/scenario';
import { useIntegration } from '@/query/integration';
import { useMessages, usePaginatedMessages } from '@/query/message';
import { usePageParam, useScenarioId } from '@/hook';
import Button from '@/component/Button';
import { errorToast } from '@/component/Toast';
import ChannelThumb from '@/component/ChannlThumb';
import { DateRangePicker, DateRange } from '@/component/dateRangePicker';
import { TypeSearchbar, DropdownItem, DropdownGroup, DropdownItems, DropdownButton } from '@/component/input';
import MessageHistoryTable from './MessageHistoryTable';
import { DownloadIcon } from '@/assets/svgs/icons';
import { ReactComponent as LoadingIcon } from "@/assets/svgs/loading.svg";


const MESSAGE_SIZE = 20;

const SEARCH_TYPE_OPTIONS: LabelKeyValueItem<MessageHistorySearchType>[] = [
    { labelKey: "i07005", value: "user_query" },
    { labelKey: "i07006", value: "bot_query" },
    { labelKey: "i07004", value: "conversation_id" },
    { labelKey: "i07010", value: "user_key" },
];

const CHANNEL_FILTER_OPTIONS: LabelKeyValueItem<MessageChannel>[] = [
    { labelKey: "b0024", value: "web" },
    { labelKey: "b0043", value: "kakaotalk" },
    { labelKey: "b0044", value: "navertalk" }
];

const FEEDBACK_FILTER_OPTIONS: LabelKeyValueItem<MessageFeedback>[] = [
    { labelKey: "i01015", value: "good" },
    { labelKey: "i01016", value: "bad" }
];

type MessageHistoryPageParam = {
    page: number;
    size: number;
    searchType: MessageHistorySearchType;
    searchKeyword: string;
    sort: "asc" | "desc";
    channel?: MessageChannel;
    feedback?: MessageFeedback;
}

interface MessageHistoryTabProps {
    period: DateRange;
    setPeriod: (period: DateRange) => void;
}

const MessageHistoryTab: React.FC<MessageHistoryTabProps> = (props) => {
    const { period, setPeriod } = props;
    const intl = useIntl();
    const scenarioId = useScenarioId();

    const tableBodyRef = useRef<HTMLDivElement>(null);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const { data: scenario } = useScenario(scenarioId);
    const { data: publishData } = useIntegration(scenarioId);

    const { param, setParam, updateCallback } = usePageParam<MessageHistoryPageParam>({
        page: 0,
        size: MESSAGE_SIZE,
        searchType: "user_query",
        searchKeyword: "",
        sort: "desc"
    });

    const {
        data: messagePages,
        hasNextPage,
        fetchNextPage
    } = usePaginatedMessages({
        scenarioId,
        size: MESSAGE_SIZE,
        sort: param.sort,
        start_date: period.start!.format("YYYY-MM-DD HH:mm:ss"),
        end_date: period.end!.format("YYYY-MM-DD HH:mm:ss"),
        searchType: param.searchType,
        condition: param.searchKeyword,
        channel: param.channel,
        feedback: param.feedback
    });

    // not automatically refetched
    const { refetch: getTotalMessages } = useMessages({
        scenarioId,
        start_date: period.start!.format("YYYY-MM-DD HH:mm:ss"),
        end_date: period.end!.format("YYYY-MM-DD HH:mm:ss"),
        searchType: param.searchType,
        condition: param.searchKeyword,
        channel: param.channel,
        feedback: param.feedback
    });

    const onScrollToTableBottom = () => {
        if (hasNextPage) {
            fetchNextPage().then();
        }
    };

    const onClickDownload = async () => {
        if (isDownloading) {
            return;
        }
        setIsDownloading(true);
        try {
            const { data: totalMessages } = await getTotalMessages();
            if (!totalMessages || !scenario) {
                return;
            }

            const excelRows = convertMessagesToExcelRows(totalMessages, scenario.properties?.message_history_custom ?? [], intl);
            const excelBlob = new Blob(
                [
                    XLSXWrite(
                        {
                            Sheets: { data: XLSXUtils.json_to_sheet(excelRows) },
                            SheetNames: ["data"]
                        }, {
                            bookType: "xlsx",
                            type: "array"
                        }
                    )
                ], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
                }
            );
            FileSaver.saveAs(excelBlob, `${scenario.name}_messages.xlsx`);
        } catch (e) {
            console.error(e);
            errorToast(intl.formatMessage({ id: "t007" }));
        } finally {
            setIsDownloading(false);
        }
    };

    const messages = useMemo(() => (
        messagePages?.pages.flatMap(page => page.content) ?? []
    ), [messagePages]);

    useEffect(() => {    
        const scrollToTop = () => {
            tableBodyRef.current?.scrollTo({
                top: 0,
            });
        };
        updateCallback(scrollToTop);
    }, [updateCallback]);

    return (
        <div className={classNames(
                 "w-full flex-1 min-h-0 flex flex-col gap-3 min-w-fit",
                 !messages?.length && "w-md-page"
             )}
        >
            <div className="flex gap-1.5">
                <DateRangePicker useTime
                                 period={period}
                                 onChangePeriod={setPeriod}
                />

                <TypeSearchbar className="w-[680px]"
                               placeholder={intl.formatMessage({ id: "i07003" })}
                               searchTypes={SEARCH_TYPE_OPTIONS}
                               onSearch={(keyword, option) => {
                                    setParam({
                                        ...param,
                                        searchType: option.value,
                                        searchKeyword: keyword
                                    });
                               }}
                />

                <span id="channel-filter-label"
                      className="sr-only"
                >
                    {intl.formatMessage({ id: "i07011" })}
                </span>
                <DropdownGroup width={128}
                               className="h-full"
                               aria-labelledby="channel-filter-label"
                >
                    {({ open, close }) => (<>
                        <DropdownButton open={open}
                                        close={close}
                                        variant="secondary"
                                        className="h-full"
                                        isSelected={!!param.channel}
                        >
                            {param.channel
                                ? <div className="flex items-center gap-1">
                                      <ChannelThumb channel={param.channel} className="size-4" />
                                      {intl.formatMessage({ id: getLabelKeyFromValue(CHANNEL_FILTER_OPTIONS, param.channel) })}
                                  </div>
                                : intl.formatMessage({ id: "i07011" })
                            }
                        </DropdownButton>
                        <DropdownItems variant="secondary"
                                       className="rounded-tr-none z-[10000]"
                        >
                            {CHANNEL_FILTER_OPTIONS
                                .filter(option => option.value === "web" || option.value in (publishData?.integrations ?? {}))
                                .map(option => (
                                    <DropdownItem key={option.value}
                                                    onClick={() => setParam({
                                                        ...param,
                                                        channel: option.value,
                                                    })}
                                    >
                                        <div className="flex items-center gap-1">
                                            <ChannelThumb channel={option.value} className="size-4" />
                                            {intl.formatMessage({ id: option.labelKey })}
                                        </div>
                                    </DropdownItem>
                                ))
                            }
                        </DropdownItems>
                    </>)}
                </DropdownGroup>
            
                <span id="feedback-filter-label"
                      className="sr-only"
                >
                    {intl.formatMessage({ id: "i07012" })}
                </span>
                <DropdownGroup width={96}
                               className="h-full"
                               aria-labelledby="feedback-filter-label"
                >
                    {({ open, close }) => (<>
                        <DropdownButton open={open}
                                        close={close}
                                        variant="secondary"
                                        className="h-full"
                                        isSelected={!!param.feedback}
                        >
                            {param.feedback ?? intl.formatMessage({ id: "i07012" })}
                        </DropdownButton>
                        <DropdownItems variant="secondary"
                                       className="rounded-tr-none z-[10000]"
                        >
                            {FEEDBACK_FILTER_OPTIONS.map(option => (
                                <DropdownItem key={option.value}
                                              onClick={() => setParam({ ...param, feedback: option.value })}
                                >
                                    {intl.formatMessage({ id: option.labelKey })}
                                </DropdownItem>
                            ))}
                        </DropdownItems>
                    </>)}
                </DropdownGroup>

                <Button variant="text"
                        className="ml-auto self-end"
                        aria-busy={isDownloading}
                        onClick={onClickDownload}
                        disabled={isDownloading}
                >
                    {isDownloading
                        ? <LoadingIcon aria-hidden="true"
                                       className="animate-spin size-3.5"
                          />
                        : <DownloadIcon aria-hidden="true"
                                        className="size-3.5"
                          />}
                    {intl.formatMessage({ id: "b0005" })}
                </Button>
            </div>

            <MessageHistoryTable messages={messages}
                                 searchKeyword={param.searchKeyword}
                                 searchType={param.searchType}
                                 sort={param.sort}
                                 additionalColumns={scenario?.properties?.message_history_custom ?? []}
                                 tableBodyRef={tableBodyRef}
                                 onScrollToBottom={onScrollToTableBottom}
                                 onClickSort={() => setParam({ ...param, sort: param.sort === "asc" ? "desc" : "asc" })}
            />
        </div>
    );
};

const NOT_EXPORT_CONTEXT_KEYS = ["EIO", "t", "user_key", "transport", "b64", "scenario_id"];
const HIGH_PRIORITY_CONTEXT_KEYS = ["channel", "user_name", "vehicle_name", "vehicle_model"];

const convertMessagesToExcelRows = (messages: SearchedMessage[], customOptions: MessageHistoryCustom[], intl: IntlShape) => {
    const objectToString = (object: any): string => {
        if (Array.isArray(object)) {
            return `[${object.map(item => objectToString(item)).join(", ")}]`;
        } else if (typeof object === "object") {
            return `{${Object.entries(object).map(([key, value]) => `${key}:${value}`).join(", ")}}`;
        }
        return `${object}`;
    };

    const getContextEntries = (context: ContextValues) => {
        const highPriority: [string, any][] = [];
        const lowPriority: [string, any][] = [];

        Object.entries(context)
            .forEach(([key, value]) => {
                if (NOT_EXPORT_CONTEXT_KEYS.includes(key)) {
                    return;
                }
                if (HIGH_PRIORITY_CONTEXT_KEYS.includes(key)) {
                    highPriority.push([key, value]);
                } else {
                    lowPriority.push([key, value]);
                }
            });
        
        highPriority.sort((lv, rv) => HIGH_PRIORITY_CONTEXT_KEYS.indexOf(lv[0]) - HIGH_PRIORITY_CONTEXT_KEYS.indexOf(rv[0]));
        lowPriority.sort((lv, rv) => lv[0].localeCompare(rv[0]));
        return [...highPriority, ...lowPriority];
    };

    const rows = messages.map(messageInfo => {
        const intent = messageInfo.context?.system?.global?.intent ?? {};
        const entity = (messageInfo.context?.system?.global?.entity) || {};

        const row: DictType<string> = {
            user_ip: messageInfo.ip || "",
            user_key: getValueFromGlobalContext(messageInfo, "user_key"),
            channel: getValueFromGlobalContext(messageInfo, "channel"),
            conversation_id: messageInfo.conversation_id || "-",
            response_count: `${messageInfo.context?.system?.global?.response_count || "-"}`,
            time: messageInfo.created_at ? UTCToLocalString(messageInfo.created_at, "YYYY-MM-DD HH:mm:ss") : "",
            query: messageInfo.query || "",
            response: getTextAndLabelFromResponse(messageInfo.messages, intl) || "",
            feedback: messageInfo.feedback || "",
            intent: intent.name || "",
            intent_score: intent.score || 0,
            entity: (entity.names || [])
                        .map((name: string) => `{@${name}|${entity[name].value.join(",")}}`)
                        .join(","),
            scene_id: `${messageInfo.scene_id}`,
            custom_id: messageInfo.scene_custom_id || "",
            paragraph_ids: (messageInfo.messages || [])
                               .slice(0, - 1)
                               .filter(message => message.paragraph_id)
                               .map(message => message.paragraph_id)
                               .join(", "),
            context: getContextEntries(messageInfo.context?.global ?? {})
                        .map(([key, value]) => `${key}: ${objectToString(value)}`)
                        .join(","),
            constants: Object.entries(messageInfo.context?.constants ?? {})
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(","),
            scene_name: messageInfo.scene_name || ""
        };

        customOptions.forEach(({ key, label }: MessageHistoryCustom) => {
            const value = getValueFromDotJoinKey(messageInfo, key);
            row[label] = typeof value !== "object" ? value : "";
        });

        return row;
    });

    return rows;
};

export default MessageHistoryTab;
